import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { useAuth } from '../../contexts/AuthContext';

const useStyles = makeStyles((theme) => ({
  carouselContainer: {
    marginBottom: theme.spacing(3),
  },
  carouselHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  carouselWrapper: {
    position: 'relative',
    overflow: 'visible', // Show side previews
    borderRadius: theme.shape.borderRadius,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  carouselTrack: {
    display: 'flex',
    transition: 'transform 0.3s ease-in-out',
    touchAction: 'pan-y pinch-zoom',
    alignItems: 'center',
    width: `${scenarios.length * 28}%`, // Dynamic width based on number of scenarios
  },
  scenarioSlide: {
    minWidth: '28%', // Narrower slides to bring side cards closer
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(0, 0.25), // Very tight padding
    transition: 'all 0.3s ease',
    opacity: 0.4,
    transform: 'scale(0.7)',
    '&.active': {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  imageContainer: {
    width: '100%',
    maxWidth: 150, // Smaller for more compact design
    aspectRatio: '1 / 1', // Square aspect ratio
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: `3px solid transparent`,
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      borderColor: theme.palette.primary.light,
      transform: 'scale(1.02)',
    },
  },
  selectedImage: {
    borderColor: theme.palette.primary.main,
    transform: 'scale(1.02)',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
  },
  imagePlaceholder: {
    fontSize: '4rem',
    color: theme.palette.grey[400],
  },
  scenarioTitle: {
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  scenarioSubtitle: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontSize: '0.875rem',
  },
  navigationButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
    zIndex: 2,
  },
  leftButton: {
    left: theme.spacing(1),
  },
  rightButton: {
    right: theme.spacing(1),
  },
  indicators: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
  },
  manualSwitchButton: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  indicator: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[300],
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  activeIndicator: {
    backgroundColor: theme.palette.primary.main,
    transform: 'scale(1.2)',
  },
  selectButton: {
    marginTop: theme.spacing(1),
    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
    color: 'white',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    borderRadius: theme.shape.borderRadius,
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    transition: 'all 0.2s',
    '&:hover': {
      background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
      transform: 'translateY(-1px)',
    },
  },
}));

const scenarios = [
  {
    id: 'athletic',
    title: '🏃 Athletic Lifestyle',
    subtitle: 'Sports & Fitness Vibes',
    emoji: '🏃‍♂️',
    prompts: {
      male: (triggerWord) => `A full body candid-style photo of ${triggerWord} walking around an upscale outdoor sports facility with tennis courts.

Walking with relaxed posture and quiet confidence, as if arriving early or winding down after a match. Expression is calm and present, glancing slightly off-frame in a natural, unposed moment.

Wearing a fitted light grey athletic t-shirt, black performance shorts, and white court shoes with orange accents. A simple bracelet on one wrist adds a subtle personal touch.

The scene is bathed in golden hour sunlight, with soft highlights and long shadows that add warmth and depth to the image. In the background, empty courts, chain-link fences, and modern buildings complete the upscale setting.

The photo feels spontaneous and authentic, with crisp detail, natural lighting, and a clean composition that blends active lifestyle and effortless style.`,
      
      female: (triggerWord) => `A full body candid-style photo of ${triggerWord} walking around an upscale outdoor sports facility with tennis courts.

Walking with relaxed posture and quiet confidence, as if arriving early or winding down after a match. Expression is calm and present, glancing slightly off-frame in a natural, unposed moment.

Wearing a fitted light grey athletic tank top, black athletic leggings, and white court shoes with orange accents. A simple bracelet on one wrist and hair pulled back in a casual ponytail add personal touches.

The scene is bathed in golden hour sunlight, with soft highlights and long shadows that add warmth and depth to the image. In the background, empty courts, chain-link fences, and modern buildings complete the upscale setting.

The photo feels spontaneous and authentic, with crisp detail, natural lighting, and a clean composition that blends active lifestyle and effortless style.`
    }
  },
  {
    id: 'urban_professional',
    title: '☕ Urban Professional',
    subtitle: 'Business Casual in the City',
    emoji: '💼',
    prompts: {
      male: (triggerWord) => `A full body candid-style photo of ${triggerWord} walking through a trendy urban coffee district during the late afternoon.

Striding confidently along a tree-lined sidewalk, passing modern storefronts with large glass windows and minimalist signage. Expression is focused yet relaxed, as if heading to or from an important meeting.

Wearing a navy blue blazer over a crisp white button-down shirt, dark charcoal chinos, and brown leather loafers. A sleek leather messenger bag crosses the shoulder, and a simple silver watch catches the light.

The scene is lit by warm afternoon sun filtering through urban tree canopy, creating dappled light and shadow patterns on the concrete. In the background, modern architecture mixes with classic brick buildings and empty sidewalk cafes.

The photo captures the energy of professional city life with an authentic, documentary-style feel.`,
      
      female: (triggerWord) => `A full body candid-style photo of ${triggerWord} walking through a trendy urban coffee district during the late afternoon.

Striding confidently along a tree-lined sidewalk, passing modern storefronts with large glass windows and minimalist signage. Expression is focused yet relaxed, as if heading to or from an important meeting.

Wearing a tailored navy blazer over a silk blouse, dark straight-leg trousers, and elegant block heels. A structured leather handbag rests on the shoulder, and delicate jewelry catches the light.

The scene is lit by warm afternoon sun filtering through urban tree canopy, creating dappled light and shadow patterns on the concrete. In the background, modern architecture mixes with classic brick buildings and empty sidewalk cafes.

The photo captures the energy of professional city life with an authentic, documentary-style feel.`
    }
  },
  {
    id: 'city_explorer',
    title: '🌆 City Explorer',
    subtitle: 'Weekend Adventure Mode',
    emoji: '🗺️',
    prompts: {
      male: (triggerWord) => `A full body candid-style photo of ${triggerWord} exploring a vibrant neighborhood market on a sunny weekend afternoon.

Moving casually between colorful vendor stalls selling fresh produce, artisanal goods, and street food, occasionally stopping to examine interesting items. Posture is relaxed and curious, embodying the unhurried pace of weekend exploration. Expression shows genuine interest and enjoyment, smiling slightly while taking in the atmosphere.

Wearing a comfortable fitted henley shirt in forest green, well-worn dark jeans, and clean white sneakers. A canvas crossbody bag holds essentials, and sunglasses rest casually on head.

The scene is alive with natural sunlight filtering through colorful market umbrellas and awnings, creating warm, inviting lighting. The background shows empty market stalls and quiet vendor displays in the distance.

The photo feels like a genuine moment captured during a perfect solo weekend adventure.`,
      
      female: (triggerWord) => `A full body candid-style photo of ${triggerWord} exploring a vibrant neighborhood market on a sunny weekend afternoon.

Moving casually between colorful vendor stalls selling fresh produce, artisanal goods, and street food, occasionally stopping to examine interesting items. Posture is relaxed and curious, embodying the unhurried pace of weekend exploration. Expression shows genuine interest and enjoyment, smiling slightly while taking in the atmosphere.

Wearing a comfortable fitted sweater in soft cream, well-fitted dark jeans, and stylish white sneakers. A woven tote bag holds essentials, and sunglasses rest casually on head with hair flowing naturally.

The scene is alive with natural sunlight filtering through colorful market umbrellas and awnings, creating warm, inviting lighting. The background shows empty market stalls and quiet vendor displays in the distance.

The photo feels like a genuine moment captured during a perfect solo weekend adventure.`
    }
  },
  {
    id: 'coastal_casual',
    title: '🏖️ Coastal Casual',
    subtitle: 'Relaxed Beachside Vibes',
    emoji: '🌊',
    prompts: {
      male: (triggerWord) => `A full body candid-style photo of ${triggerWord} strolling along an upscale beachside promenade during golden hour.

Walking leisurely on weathered wooden boardwalk planks, with the ocean visible in the background and modern beach houses dotting the coastline. Posture is completely relaxed, hands occasionally in pockets, embodying the carefree spirit of coastal living. Expression is content and peaceful, occasionally glancing toward the ocean or the colorful sunset sky.

Wearing a soft linen button-up shirt in light blue, rolled up at the sleeves, paired with khaki shorts and sneakers without socks. A simple rope bracelet and vintage watch complete the coastal aesthetic.

The scene is bathed in warm, golden sunset light that reflects off the water and creates a soft glow. In the background, empty beach houses and swaying beach grass complete the serene setting.

The photo captures the effortless charm of coastal lifestyle with perfect natural lighting.`,
      
      female: (triggerWord) => `A full body candid-style photo of ${triggerWord} strolling along an upscale beachside promenade during golden hour.

Walking leisurely on weathered wooden boardwalk planks, with the ocean visible in the background and modern beach houses dotting the coastline. Posture is completely relaxed, hands occasionally in pockets, embodying the carefree spirit of coastal living. Expression is content and peaceful, occasionally glancing toward the ocean or the colorful sunset sky.

Wearing a flowing linen blouse in soft white, paired with high-waisted linen pants and strappy sandals. Delicate layered necklaces and a simple watch complete the coastal aesthetic, with hair gently tousled by the sea breeze.

The scene is bathed in warm, golden sunset light that reflects off the water and creates a soft glow. In the background, empty beach houses and swaying beach grass complete the serene setting.

The photo captures the effortless charm of coastal lifestyle with perfect natural lighting.`
    }
  },
  {
    id: 'creative_professional',
    title: '🎨 Creative Professional',
    subtitle: 'Modern Creative Energy',
    emoji: '💡',
    prompts: {
      male: (triggerWord) => `A full body candid-style photo of ${triggerWord} moving through a bright, modern creative workspace.

Navigating between standing desks, exposed brick walls covered with mood boards, and floor-to-ceiling windows that flood the space with natural light. Posture is energetic and purposeful, as if heading to work on an exciting project or presenting ideas. Expression shows creative energy and intelligence, perhaps deep in thought about work.

Wearing a perfectly fitted charcoal sweater, dark slim-fit jeans, and minimalist white leather sneakers. A modern laptop bag and wireless headphones around neck suggest a tech-savvy creative lifestyle.

The scene is lit by abundant natural light from large windows, supplemented by trendy pendant lights and the glow of computer screens. In the background, empty standing desks and whiteboards complete the modern workspace setting.

The photo feels authentic and contemporary, capturing the dynamic atmosphere of creative work.`,
      
      female: (triggerWord) => `A full body candid-style photo of ${triggerWord} moving through a bright, modern creative workspace.

Navigating between standing desks, exposed brick walls covered with mood boards, and floor-to-ceiling windows that flood the space with natural light. Posture is energetic and purposeful, as if heading to work on an exciting project or presenting ideas. Expression shows creative energy and intelligence, perhaps deep in thought about work.

Wearing a stylish oversized cardigan in soft grey, fitted black leggings, and trendy white sneakers. A sleek laptop bag and wireless headphones around neck suggest a tech-savvy creative lifestyle, with hair styled in a modern, effortless way.

The scene is lit by abundant natural light from large windows, supplemented by trendy pendant lights and the glow of computer screens. In the background, empty standing desks and whiteboards complete the modern workspace setting.

The photo feels authentic and contemporary, capturing the dynamic atmosphere of creative work.`
    }
  },
  {
    id: 'evening_social',
    title: '🌃 Evening Social',
    subtitle: 'Sophisticated Night Out',
    emoji: '🥂',
    prompts: {
      male: (triggerWord) => `A full body candid-style photo of ${triggerWord} arriving at an upscale rooftop venue during blue hour, with the city skyline twinkling in the background.

Walking confidently across the modern outdoor space, past elegant lounge seating and ambient lighting, as if arriving for an evening out. Posture exudes quiet confidence and sophistication, moving with the ease of someone comfortable in upscale settings. Expression is warm and anticipatory, simply taking in the beautiful evening atmosphere.

Wearing a sharp dark navy button-up shirt with sleeves rolled up, well-tailored dark jeans, and polished brown leather shoes. A classic watch completes the evening look.

The scene is illuminated by warm ambient lighting from string lights, modern fixtures, and the soft glow of the city below. In the background, empty elegant seating areas and the city lights create a stunning backdrop.

The photo captures the sophisticated energy of urban nightlife with elegant, cinematic lighting.`,
      
      female: (triggerWord) => `A full body candid-style photo of ${triggerWord} arriving at an upscale rooftop venue during blue hour, with the city skyline twinkling in the background.

Walking confidently across the modern outdoor space, past elegant lounge seating and ambient lighting, as if arriving for an evening out. Posture exudes quiet confidence and sophistication, moving with the ease of someone comfortable in upscale settings. Expression is warm and anticipatory, simply taking in the beautiful evening atmosphere.

Wearing an elegant black blouse with subtle detailing, well-fitted dark jeans, and stylish heeled ankle boots. Delicate jewelry and a classic watch complete the sophisticated evening look.

The scene is illuminated by warm ambient lighting from string lights, modern fixtures, and the soft glow of the city below. In the background, empty elegant seating areas and the city lights create a stunning backdrop.

The photo captures the sophisticated energy of urban nightlife with elegant, cinematic lighting.`
    }
  }
];

function VisualScenarioCarousel({ onScenarioSelect, onSwitchToManual, triggerWord = "USER_xyz" }) {
  const classes = useStyles();
  const { userData } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const trackRef = useRef(null);
  const startX = useRef(0);
  const isDragging = useRef(false);

  // Handle touch/mouse events for swiping
  const handleStart = (clientX) => {
    startX.current = clientX;
    isDragging.current = true;
  };

  const handleMove = (clientX) => {
    if (!isDragging.current) return;
    
    const deltaX = clientX - startX.current;
    if (Math.abs(deltaX) > 50) { // Minimum swipe distance
      if (deltaX > 0 && currentIndex > 0) {
        handlePrevious();
      } else if (deltaX < 0 && currentIndex < scenarios.length - 1) {
        handleNext();
      }
      isDragging.current = false;
    }
  };

  const handleEnd = () => {
    isDragging.current = false;
  };

  // Touch events
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : scenarios.length - 1);
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev < scenarios.length - 1 ? prev + 1 : 0);
  };

  const handleIndicatorClick = (index) => {
    setCurrentIndex(index);
  };

  const handleImageClick = (scenario, index) => {
    // Center the clicked image
    setCurrentIndex(index);
    
    setSelectedScenario(scenario);
    
    // Use user's gender preference, default to male if not specified
    const userGender = userData?.demographics?.gender || 'male';
    const genderToUse = (userGender === 'female') ? 'female' : 'male'; // Default to male for 'other' or undefined
    
    const prompt = scenario.prompts[genderToUse](triggerWord);
    
    onScenarioSelect(prompt);
  };

  return (
    <div className={classes.carouselContainer}>
      <div className={classes.carouselHeader}>
        <Typography variant="h6" gutterBottom>
          📸 Choose Your Scenario
        </Typography>

      </div>

      <div className={classes.carouselWrapper}>
        {/* Carousel Track */}
        <div
          ref={trackRef}
          className={classes.carouselTrack}
          style={{
            transform: `translateX(calc(50% - ${(currentIndex + 0.5) * 28}%))` // Adjusted for 28% width slides
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
        >
          {scenarios.map((scenario, index) => (
            <div 
              key={scenario.id} 
              className={`${classes.scenarioSlide} ${index === currentIndex ? 'active' : ''}`}
            >
              <Paper
                className={`${classes.imageContainer} ${
                  selectedScenario?.id === scenario.id ? classes.selectedImage : ''
                }`}
                onClick={() => handleImageClick(scenario, index)}
                elevation={index === currentIndex ? 4 : 1}
              >
                {/* Image Placeholder */}
                <Typography className={classes.imagePlaceholder}>
                  {scenario.emoji}
                </Typography>
              </Paper>
              
              <Typography variant="subtitle1" className={classes.scenarioTitle}>
                {scenario.title}
              </Typography>
              
              <Typography className={classes.scenarioSubtitle}>
                {scenario.subtitle}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default VisualScenarioCarousel;